// ProductSingleView.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import "./ProductSingleView.css";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";
import { getProductById, getRelatedProducts } from "../../data/products-data";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import Modal from "../../components/Modal/Modal";
import { Helmet } from "react-helmet-async";

const ProductSingleView = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  useScrollAnimation();

  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [activeTab, setActiveTab] = useState("description");
  const [imageError, setImageError] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false); // State for modal
  // Function to open the modal
  const openModal = () => {
    setIsModalOpen(true);
  };

  // Function to close the modal
  const closeModal = () => {
    setIsModalOpen(false);
  };
  useEffect(() => {
    const foundProduct = getProductById(productId);
    if (foundProduct) {
      setProduct(foundProduct);
      setRelatedProducts(getRelatedProducts(foundProduct));
    } else {
      navigate("/not-found"); // This should match your route
    }

    // Add animation classes to elements on load
    const animateElements = document.querySelectorAll(".animate-on-load");
    animateElements.forEach((element, index) => {
      element.style.animationDelay = `${0.2 + index * 0.1}s`;
    });

    window.scrollTo(0, 0);
  }, [productId, navigate]);

  if (!product) {
    return (
      <>
        <Header />
        <div className="product-single-page">
          <div className="container">
            <div className="not-found animate-on-scroll">
              <h2>Product Not Found</h2>
              <p>The requested product does not exist.</p>
              <button className="btn btn-primary" onClick={() => navigate("/")}>
                Go Back Home
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <div>
      {" "}
      <Helmet>
        <title>
          {product.mainCategory} | Avvoka - Exporters of Fresh Produce, Tea &
          Coffee
        </title>
        <meta
          name="description"
          content={`Learn more about Avvoka's premium ${product.mainCategory}. Exported from Kenya with freshness, quality, and sustainability guaranteed.`}
        />
        <meta
          name="keywords"
          content={`${product.mainCategory}, Avvoka ${product.mainCategory}, Kenyan ${product.mainCategory} export, fresh ${product.mainCategory}, premium ${product.mainCategory}, export company Kenya`}
        />
        <meta
          property="og:title"
          content={`${product.mainCategory} | Avvoka - Premium Export`}
        />
        <meta
          property="og:description"
          content={`Discover Avvoka's high-quality ${product.mainCategory}, exported from Kenya to global markets. Fresh, sustainable, and reliable delivery.`}
        />
        <meta property="og:type" content="product" />
        <meta
          property="og:url"
          content={`https://avvoka.co.ke/products/${product.slug}`}
        />
        <meta
          property="og:image"
          content={`https://avvoka.co.ke/assets/images/products/${product.image}`}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@AvvokaExports" />
        <meta
          name="theme-color"
          content="#4CAF50"
          media="(prefers-color-scheme: light)"
        />
        <meta
          name="theme-color"
          content="#2E7D32"
          media="(prefers-color-scheme: dark)"
        />
      </Helmet>
      <Header />
      <div className="product-single-page">
        {/* Breadcrumb */}
        <section className="breadcrumb-section">
          <div className="container">
            <nav className="breadcrumb animate-on-load">
              <Link to="/">Home</Link>
              <span>/</span>
              <Link to={`/products/${product.mainCategory}/all`}>
                {product.mainCategory}
              </Link>
              <span>/</span>
              <span>{product.name}</span>
            </nav>
          </div>
        </section>

        {/* Product Hero */}
        <section className="product-hero">
          <div className="container">
            <div className="product-hero-grid">
              <div className="product-image-container ">
                <div className="product-main-image">
                  <img
                    src={product.image}
                    alt={product.name}
                    onError={() => setImageError(true)}
                  />
                  {imageError && (
                    <div className="image-placeholder large">
                      <span>{product.name.split(" ")[0]}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="product-info ">
                <h1>{product.name}</h1>
                <p className="product-category">
                  {product.mainCategory} • {product.subcategory}
                </p>
                <p className="product-short-desc">{product.description}</p>

                <div className="product-highlights">
                  <div className="highlight-item">
                    <div className="highlight-icon">🌱</div>
                    <div className="highlight-text">
                      <span>Organic Certified</span>
                      <small>Naturally grown</small>
                    </div>
                  </div>
                  <div className="highlight-item">
                    <div className="highlight-icon">📦</div>
                    <div className="highlight-text">
                      <span>Export Ready</span>
                      <small>Global standards</small>
                    </div>
                  </div>
                  <div className="highlight-item">
                    <div className="highlight-icon">🚢</div>
                    <div className="highlight-text">
                      <span>Worldwide Shipping</span>
                      <small>Fast & reliable</small>
                    </div>
                  </div>
                </div>

                <div className="product-specs-overview">
                  <div className="spec-item">
                    <span className="spec-label">Minimum Order:</span>
                    <span className="spec-value">{product.minOrder}</span>
                  </div>
                  <div className="spec-item">
                    <span className="spec-label">Price Range:</span>
                    <span className="spec-value">{product.priceRange}</span>
                  </div>
                  <div className="spec-item">
                    <span className="spec-label">Availability:</span>
                    <span className="spec-value">{product.seasonality}</span>
                  </div>
                </div>

                <div className="product-action-buttons">
                  <button className="btn btn-primary">Request Sample</button>
                  <button className="btn btn-secondary" onClick={openModal}>
                    Get Quote
                  </button>
                  <button className="btn btn-outline">Contact Sales</button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Product Details Tabs */}
        <section className="product-details-section">
          <div className="container">
            <div className="tabs-container animate-on-scroll">
              <div className="tabs-header">
                <button
                  className={`tab-btn ${
                    activeTab === "description" ? "active" : ""
                  }`}
                  onClick={() => setActiveTab("description")}
                >
                  Description
                </button>
                <button
                  className={`tab-btn ${
                    activeTab === "specifications" ? "active" : ""
                  }`}
                  onClick={() => setActiveTab("specifications")}
                >
                  Specifications
                </button>
                <button
                  className={`tab-btn ${
                    activeTab === "shipping" ? "active" : ""
                  }`}
                  onClick={() => setActiveTab("shipping")}
                >
                  Shipping & Handling
                </button>
                {product.nutritionalInfo && (
                  <button
                    className={`tab-btn ${
                      activeTab === "nutrition" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("nutrition")}
                  >
                    Nutritional Info
                  </button>
                )}
              </div>

              <div className="tab-content">
                {activeTab === "description" && (
                  <div className="tab-pane ">
                    <h3>Product Details</h3>
                    <p>{product.detailedDescription}</p>

                    {product.flavorProfile && (
                      <div className="detail-block">
                        <h4>Flavor Profile</h4>
                        <div className="flavor-profile">
                          <div className="flavor-attribute">
                            <span className="attribute-label">Acidity:</span>
                            <div className="attribute-bar">
                              <div
                                className="attribute-fill"
                                style={{
                                  width: `${
                                    product.flavorProfile.acidity === "Bright"
                                      ? "80%"
                                      : "60%"
                                  }`,
                                }}
                              ></div>
                            </div>
                            <span className="attribute-value">
                              {product.flavorProfile.acidity}
                            </span>
                          </div>
                          <div className="flavor-attribute">
                            <span className="attribute-label">Body:</span>
                            <div className="attribute-bar">
                              <div
                                className="attribute-fill"
                                style={{
                                  width: `${
                                    product.flavorProfile.body === "Full"
                                      ? "90%"
                                      : "70%"
                                  }`,
                                }}
                              ></div>
                            </div>
                            <span className="attribute-value">
                              {product.flavorProfile.body}
                            </span>
                          </div>
                          {product.flavorProfile.tastingNotes && (
                            <div className="tasting-notes">
                              <span className="attribute-label">
                                Tasting Notes:
                              </span>
                              <span className="attribute-value">
                                {product.flavorProfile.tastingNotes}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === "specifications" && (
                  <div className="tab-pane">
                    <h3>Technical Specifications</h3>
                    <div className="specs-grid">
                      {Object.entries(product.specifications).map(
                        ([key, value]) => (
                          <div key={key} className="spec-item-detailed">
                            <span className="spec-key">
                              {key
                                .replace(/([A-Z])/g, " $1")
                                .replace(/^./, (str) => str.toUpperCase())}
                              :
                            </span>
                            <span className="spec-value">
                              {Array.isArray(value) ? value.join(", ") : value}
                            </span>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}

                {activeTab === "shipping" && (
                  <div className="tab-pane ">
                    <h3>Shipping & Handling Information</h3>
                    <div className="shipping-info">
                      <div className="shipping-method">
                        <h4>Shipping Methods</h4>
                        <p>{product.shippingInfo}</p>
                      </div>
                      <div className="packaging-details">
                        <h4>Packaging</h4>
                        <p>
                          Our products are carefully packaged to maintain
                          freshness and quality during transit:
                        </p>
                        <ul>
                          <li>Food-grade packaging materials</li>
                          <li>
                            Temperature-controlled containers for perishables
                          </li>
                          <li>
                            Proper ventilation to prevent moisture buildup
                          </li>
                          <li>
                            Clear labeling with product information and handling
                            instructions
                          </li>
                        </ul>
                      </div>
                      <div className="handling-instructions">
                        <h4>Handling Instructions</h4>
                        <p>Upon receipt, please:</p>
                        <ol>
                          <li>Inspect packaging for any damage</li>
                          <li>Store at recommended temperatures</li>
                          <li>
                            Rotate stock using FIFO (First In, First Out) method
                          </li>
                          <li>
                            Follow any specific handling instructions provided
                          </li>
                        </ol>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "nutrition" && product.nutritionalInfo && (
                  <div className="tab-pane ">
                    <h3>Nutritional Information</h3>
                    <div className="nutrition-facts">
                      <div className="nutrition-header">
                        <h4>Nutrition Facts</h4>
                        <p>Serving Size: 100g</p>
                      </div>
                      <div className="nutrition-grid">
                        {Object.entries(product.nutritionalInfo).map(
                          ([key, value]) => (
                            <div key={key} className="nutrition-item">
                              <span className="nutrition-label">
                                {key
                                  .replace(/([A-Z])/g, " $1")
                                  .replace(/^./, (str) => str.toUpperCase())}
                                :
                              </span>
                              <span className="nutrition-value">{value}</span>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Certifications Section */}
        <section className="certifications-section">
          <div className="container">
            <div className="section-header animate-on-scroll">
              <h2>Quality Certifications</h2>
              <p>Our commitment to quality and sustainability</p>
            </div>
            <div className="certifications-grid animate-on-scroll delay-1">
              <div className="certification-item">
                <div className="certification-icon">✅</div>
                <h4>GlobalG.A.P. Certified</h4>
                <p>
                  Good Agricultural Practices ensuring safe and sustainable
                  farming
                </p>
              </div>
              <div className="certification-item">
                <div className="certification-icon">✅</div>
                <h4>HACCP</h4>
                <p>Hazard Analysis Critical Control Point for food safety</p>
              </div>
              <div className="certification-item">
                <div className="certification-icon">✅</div>
                <h4>Organic Certified</h4>
                <p>Grown without synthetic pesticides or fertilizers</p>
              </div>
              <div className="certification-item">
                <div className="certification-icon">✅</div>
                <h4>ISO 22000</h4>
                <p>International standard for food safety management</p>
              </div>
            </div>
          </div>
        </section>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <section className="related-products-section">
            <div className="container">
              <div className="section-header animate-on-scroll">
                <h2>Related Products</h2>
                <p>You might also be interested in these products</p>
              </div>
              <div className="products-grid">
                {relatedProducts.map((relatedProduct, index) => (
                  <div
                    key={relatedProduct.id}
                    className={`product-card animate-on-scroll delay-${
                      index % 4
                    }`}
                    onClick={() => navigate(`/product/${relatedProduct.id}`)}
                  >
                    <div className="product-image">
                      <img
                        src={relatedProduct.image}
                        alt={relatedProduct.name}
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                      <div className="image-placeholder">
                        <span>{relatedProduct.name.split(" ")[0]}</span>
                      </div>
                    </div>
                    <div className="product-content">
                      <h3>{relatedProduct.name}</h3>
                      <p>{relatedProduct.description}</p>
                      <div className="product-actions">
                        <button className="btn-learn-more">
                          Learn More
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M5 12H19M19 12L12 5M19 12L12 19"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA Section */}
        <section className="product-cta">
          <div className="container">
            <div className="cta-content">
              <h2 className="animate-right">Ready to order {product.name}?</h2>
              <p className="animate-left">
                Contact our sales team for pricing, samples, and bulk order
                information
              </p>
              <div className="cta-buttons animate-on-scroll delay-1">
                <button className="btn btn-primary">Request Quote</button>
                <button className="btn btn-secondary">Contact Sales</button>
              </div>
            </div>
          </div>
        </section>
      </div>
      {/* Modal component */}
      <Modal isOpen={isModalOpen} onClose={closeModal} />
      <Footer />
    </div>
  );
};

export default ProductSingleView;
